import { COMPONENT_CATALOG, getComponent } from "../data/catalog";
import { getMap } from "../data/maps";
import type { CsimEquipment, CsimRack, DecodedCsimSave, SaveEquipmentKind } from "./csim-save";
import { isComponentCompatibleWithRack } from "./placement";
import { applyRackSettingsToMap } from "./rack-settings";
import type { MapDefinition, MapId, Placement, Plan, RackDefinition, RackSettings } from "./types";

const MAX_NAME_LENGTH = 80;

const SITE_MAP_IDS: Readonly<Record<string, MapId>> = Object.freeze({
    garage: "garage",
    basement: "basement",
    small_colo: "small-colo",
    "small-colo": "small-colo",
    small_campus: "small-campus",
    "small-campus": "small-campus",
});

const GAME_SKU_ALIASES: Readonly<Record<string, string>> = Object.freeze({
    "arm 1u": "arm-1u",
    "arm 1u dual": "arm-dual-socket-1u",
    "arm 2u": "arm-2u",
    "arm 2u dual": "arm-dual-socket-2u",
    "balanced x86 1u": "balanced-x86-1u",
    "balanced x86 2u": "balanced-x86-2u",
    "balanced x86 2u dual": "balanced-x86-dual-socket-2u",
    "compute 1u": "compute-dense-1u",
    "compute 2u": "compute-dense-2u",
    "compute 2u dual": "compute-dual-socket-2u",
    "gpu 2x 2u": "gpu-2x-2u",
    "gpu 4x 4u": "gpu-4x-4u",
    "gpu 8x 7u": "gpu-8x-7u",
    "mainframe 4u": "mainframe-4u",
    "mainframe 7u": "mainframe-7u",
    "memory 1u": "memory-optimised-1u",
    "memory 2u": "memory-optimised-2u",
    "memory 2u dual": "memory-optimised-dual-socket-2u",
    "memory optimised 1u": "memory-optimised-1u",
    "memory optimised 2u": "memory-optimised-2u",
    "memory optimised 2u dual": "memory-optimised-dual-socket-2u",
    "risc-v 2u": "risc-v-2u",
    "risc-v 4u": "risc-v-4u",
    "storage 1u": "storage-1u",
    "storage 2u": "storage-2u",
    "storage 4u": "storage-4u",
    "storage 7u": "storage-7u",
    "16xgbe access": "switch-16x1g-rj45-managed-l2",
    "24xgbe access": "switch-24x1g-rj45-managed-l2",
    "24-port access gbe": "switch-24x1g-rj45-managed-l2",
    "24xgbe + 2xsfp+ access": "switch-24x1g-rj45-2x10g-sfp-plus-managed-l2",
    "48xgbe + 4xsfp+ access": "switch-48x1g-rj45-4x10g-sfp-plus-managed-l2",
    "24xgbe + 4xsfp+ mlag access": "switch-24x1g-4x10g-mlag",
    "48xgbe + 4xsfp+ mlag access": "switch-48x1g-4x10g-mlag",
    "24x10g + 2x25g access": "switch-24x10g-sfp-plus-2x25g-sfp28-managed-l3",
    "36x10g + 2x25g access": "switch-36x10g-sfp-plus-2x25g-sfp28-managed-l3",
    "48x10g tor": "switch-48x10g-sfp-plus-managed-l3",
    "48x10g + 4x40g tor": "switch-48x10g-sfp-plus-4x40g-qsfp-plus-managed-l3",
    "48x10g + 4x40g mlag tor": "switch-48x10g-4x40g-tor-mlag",
    "24x40g + 4x100g aggregation": "switch-24x40g-qsfp-plus-4x100g-qsfp28-managed-l3",
    "48x25g + 6x100g leaf": "switch-48x25g-sfp28-6x100g-qsfp28-managed-l3",
    "48x25g + 6x100g mlag leaf": "switch-48x25g-6x100g-leaf-mlag",
    "32x100g spine": "switch-32x100g-qsfp28-managed-l3",
    "32x200g spine": "switch-32x200g-qsfp56-managed-l3",
    "32x400g spine": "switch-2x25g-sfp28-32x400g-qsfp-dd-managed-l3",
    "8x1g gateway": "gateway-8x1g-router",
    "4x1g + 2x10g gateway": "gateway-4x1g-2x10g-router",
    "8x10g + 2x25g gateway": "gateway-8x10g-2x25g-router-firewall",
    "16x25g + 2x100g gateway": "gateway-16x25g-2x100g-border-router",
    "16x100g + 8x400g gateway": "gateway-16x100g-8x400g-hyperscale-edge",
    "2x1g + 2x10g security gateway": "security-2x1g-rj45-2x10g-sfp-plus",
    "2x25g + 2x10g security gateway": "security-2x10g-sfp-plus-2x25g-sfp28",
    "4x10g security gateway": "security-4x10g-sfp-plus",
    "4x100g security gateway": "security-4x100g-qsfp28",
    siem: "siem-2u",
    "pp-24 patch panel": "patch-panel-pp-24-patch-panel",
});

export interface UnsupportedSaveSku {
    readonly name: string;
    readonly count: number;
}

export type SavegameSkipReason =
    | "unsupported-sku"
    | "rack-not-found"
    | "size-mismatch"
    | "rack-incompatible"
    | "position-conflict";

export interface SkippedSaveItem {
    readonly name: string;
    readonly reason: SavegameSkipReason;
    readonly count: number;
}

export interface SavegameImportSummary {
    readonly source: "savegame" | "api";
    readonly saveVersion: number;
    readonly mapId: MapId;
    readonly mapName: string;
    readonly saveRackCount: number;
    readonly matchedRackCount: number;
    readonly placementCount: number;
    readonly fixedEquipmentCount: number;
    readonly skippedEquipmentCount: number;
    readonly importedByKind: Readonly<Record<Exclude<SaveEquipmentKind, "edd">, number>>;
    readonly unsupportedSkus: readonly UnsupportedSaveSku[];
    readonly skippedItems: readonly SkippedSaveItem[];
}

export interface SavegamePlanResult {
    readonly plan: Plan;
    readonly summary: SavegameImportSummary;
}

export type SavegameImportStage = "reading" | "decoding" | "mapping" | "validating" | "complete";

export interface SavegameImportProgressMessage {
    readonly type: "progress";
    readonly stage: SavegameImportStage;
    readonly percent: number;
}

export interface SavegameImportCompleteMessage {
    readonly type: "complete";
    readonly result: SavegamePlanResult;
}

export interface SavegameImportErrorMessage {
    readonly type: "error";
    readonly code: SavegameImportErrorCode;
}

export type SavegameImportErrorCode =
    | "empty-file"
    | "file-too-large"
    | "read-failed"
    | "unsupported-format"
    | "unsupported-site"
    | "mapping-failed"
    | "worker-failed";

export type SavegameImportWorkerMessage =
    | SavegameImportProgressMessage
    | SavegameImportCompleteMessage
    | SavegameImportErrorMessage;

export interface SavegameImportWorkerRequest {
    readonly file: File;
}

export class SavegameImportError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = "SavegameImportError";
    }
}

function normalizeSkuName(value: string): string {
    return value.normalize("NFKC").toLowerCase().replace(/[×�]/g, "x").replace(/·.*$/g, "").replace(/\s+/g, " ").trim();
}

function createCatalogLookup(): ReadonlyMap<string, string> {
    const lookup = new Map<string, string>();
    for (const [alias, componentId] of Object.entries(GAME_SKU_ALIASES)) {
        lookup.set(normalizeSkuName(alias), componentId);
    }
    for (const component of COMPONENT_CATALOG) {
        const normalized = normalizeSkuName(component.name);
        if (!lookup.has(normalized)) {
            lookup.set(normalized, component.id);
        }
    }
    return lookup;
}

const CATALOG_BY_GAME_SKU = createCatalogLookup();

function mapIdForSite(siteId: string): MapId {
    const mapId = SITE_MAP_IDS[siteId.toLowerCase()];
    if (mapId === undefined) {
        throw new SavegameImportError(`The Uptime site “${siteId}” is not supported by this planner.`);
    }
    return mapId;
}

function resolveRack(map: MapDefinition, rack: CsimRack, usedWallRackIds: Set<string>): RackDefinition | undefined {
    if (rack.wallMount) {
        const wallRack = map.racks.find(
            (candidate) => candidate.type === "wall-4u" && !usedWallRackIds.has(candidate.id),
        );
        if (wallRack !== undefined) {
            usedWallRackIds.add(wallRack.id);
        }
        return wallRack;
    }
    if (rack.floorSlot === null) {
        return undefined;
    }
    return map.racks.find((candidate) => candidate.gameSlot === rack.floorSlot && candidate.type !== "wall-4u");
}

function mapRacks(
    map: MapDefinition,
    saveRacks: readonly CsimRack[],
    source: SavegameImportSummary["source"],
): {
    readonly rackIds: ReadonlyMap<number, string>;
    readonly rackSettings: readonly RackSettings[];
} {
    const rackIds = new Map<number, string>();
    const rackSettings: RackSettings[] = [];
    const usedWallRackIds = new Set<string>();
    for (const saveRack of saveRacks) {
        const rack = resolveRack(map, saveRack, usedWallRackIds);
        const supportedSize =
            rack !== undefined &&
            (rack.sizeU === saveRack.capacityU ||
                (rack.type !== "wall-4u" && (saveRack.capacityU === 24 || saveRack.capacityU === 42)));
        if (rack === undefined || !supportedSize || rackIds.has(saveRack.gameId)) {
            continue;
        }
        rackIds.set(saveRack.gameId, rack.id);
        const name = saveRack.name.trim().slice(0, MAX_NAME_LENGTH);
        const sizeU = saveRack.capacityU === 24 || saveRack.capacityU === 42 ? saveRack.capacityU : undefined;
        rackSettings.push({
            rackId: rack.id,
            ...(name.length > 0 && name !== rack.name ? { name } : {}),
            showNameAsComponentPrefix: false,
            ...(sizeU === undefined ? {} : { sizeU }),
            sizeSource: source,
        });
    }
    return { rackIds, rackSettings };
}

function createOccupancy(map: MapDefinition): Map<string, boolean[]> {
    const occupancy = new Map<string, boolean[]>();
    for (const rack of map.racks) {
        const units = Array.from({ length: rack.sizeU + 1 }, () => false);
        for (const fixed of rack.fixedComponents ?? []) {
            const definition = getComponent(fixed.componentId);
            if (definition === undefined) {
                continue;
            }
            for (let unit = fixed.startU; unit < fixed.startU + definition.sizeU && unit <= rack.sizeU; unit += 1) {
                units[unit] = true;
            }
        }
        occupancy.set(rack.id, units);
    }
    return occupancy;
}

function hasFreeUnits(units: readonly boolean[], startU: number, sizeU: number): boolean {
    if (startU < 1 || sizeU < 1 || startU + sizeU > units.length) {
        return false;
    }
    for (let unit = startU; unit < startU + sizeU; unit += 1) {
        if (units[unit] !== false) {
            return false;
        }
    }
    return true;
}

function occupyUnits(units: boolean[], startU: number, sizeU: number): void {
    for (let unit = startU; unit < startU + sizeU; unit += 1) {
        units[unit] = true;
    }
}

function placementId(item: CsimEquipment): string {
    return `save-${item.kind}-${item.gameId}`;
}

function buildPlacements(
    map: MapDefinition,
    equipment: readonly CsimEquipment[],
    rackIds: ReadonlyMap<number, string>,
): {
    readonly placements: readonly Placement[];
    readonly fixedEquipmentCount: number;
    readonly skippedEquipmentCount: number;
    readonly importedByKind: Readonly<Record<Exclude<SaveEquipmentKind, "edd">, number>>;
    readonly unsupportedSkus: readonly UnsupportedSaveSku[];
    readonly skippedItems: readonly SkippedSaveItem[];
} {
    const occupancy = createOccupancy(map);
    const placements: Placement[] = [];
    const unsupported = new Map<string, number>();
    const skipped = new Map<string, SkippedSaveItem>();
    const importedByKind = { host: 0, switch: 0, gateway: 0, security: 0, "patch-panel": 0 };
    let fixedEquipmentCount = 0;
    let skippedEquipmentCount = 0;
    const sorted = [...equipment].sort(
        (left, right) => left.gameRackId - right.gameRackId || left.startU - right.startU,
    );
    for (const item of sorted) {
        if (item.kind === "edd") {
            fixedEquipmentCount += 1;
            continue;
        }
        const componentId = CATALOG_BY_GAME_SKU.get(normalizeSkuName(item.skuName));
        const rackId = rackIds.get(item.gameRackId);
        const rack = rackId === undefined ? undefined : map.racks.find((candidate) => candidate.id === rackId);
        const definition = componentId === undefined ? undefined : getComponent(componentId);
        const units = rackId === undefined ? undefined : occupancy.get(rackId);
        if (definition === undefined) {
            unsupported.set(item.skuName, (unsupported.get(item.skuName) ?? 0) + 1);
            recordSkippedItem(skipped, item.skuName, "unsupported-sku");
            skippedEquipmentCount += 1;
            continue;
        }
        if (rack === undefined || units === undefined) {
            recordSkippedItem(skipped, item.skuName, "rack-not-found");
            skippedEquipmentCount += 1;
            continue;
        }
        if (definition.sizeU !== item.sizeU) {
            recordSkippedItem(skipped, item.skuName, "size-mismatch");
            skippedEquipmentCount += 1;
            continue;
        }
        if (!isComponentCompatibleWithRack(rack, definition)) {
            recordSkippedItem(skipped, item.skuName, "rack-incompatible");
            skippedEquipmentCount += 1;
            continue;
        }
        if (!hasFreeUnits(units, item.startU, definition.sizeU)) {
            recordSkippedItem(skipped, item.skuName, "position-conflict");
            skippedEquipmentCount += 1;
            continue;
        }
        occupyUnits(units, item.startU, definition.sizeU);
        const localName = item.displayName.trim().slice(0, MAX_NAME_LENGTH);
        placements.push({
            id: placementId(item),
            componentId: definition.id,
            rackId: rack.id,
            startU: item.startU,
            ...(localName.length === 0 ? {} : { localName }),
        });
        importedByKind[item.kind] += 1;
    }
    const unsupportedSkus = [...unsupported.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name, "en"));
    const skippedItems = [...skipped.values()].sort(
        (left, right) => right.count - left.count || left.name.localeCompare(right.name, "en"),
    );
    return { placements, fixedEquipmentCount, skippedEquipmentCount, importedByKind, unsupportedSkus, skippedItems };
}

function recordSkippedItem(skipped: Map<string, SkippedSaveItem>, name: string, reason: SavegameSkipReason): void {
    const key = `${reason}\u0000${name}`;
    const current = skipped.get(key);
    skipped.set(key, { name, reason, count: (current?.count ?? 0) + 1 });
}

function projectName(fileName: string): string {
    const withoutExtension = fileName.replace(/\.save$/i, "").trim();
    return (withoutExtension.length === 0 ? "Imported Uptime Save" : withoutExtension).slice(0, MAX_NAME_LENGTH);
}

function createPlanFromDecoded(
    decoded: DecodedCsimSave,
    name: string,
    source: SavegameImportSummary["source"],
): SavegamePlanResult {
    const mapId = mapIdForSite(decoded.siteId);
    const map = getMap(mapId);
    if (map === undefined) {
        throw new SavegameImportError(`The map for Uptime site “${decoded.siteId}” is unavailable.`);
    }
    const mappedRacks = mapRacks(map, decoded.racks, source);
    if (mappedRacks.rackIds.size === 0) {
        throw new SavegameImportError("No savegame racks could be matched to the selected official map.");
    }
    const configuredMap = applyRackSettingsToMap(map, mappedRacks.rackSettings);
    const built = buildPlacements(configuredMap, decoded.equipment, mappedRacks.rackIds);
    const timestamp = new Date().toISOString();
    const plan: Plan = {
        project: {
            id: `save-${crypto.randomUUID()}`,
            name,
            mapId,
            scenarioId: "sandbox",
            createdAt: timestamp,
            updatedAt: timestamp,
        },
        placements: built.placements,
        rackSettings: mappedRacks.rackSettings,
    };
    return {
        plan,
        summary: {
            source,
            saveVersion: decoded.version,
            mapId,
            mapName: map.name,
            saveRackCount: decoded.racks.length,
            matchedRackCount: mappedRacks.rackIds.size,
            placementCount: built.placements.length,
            fixedEquipmentCount: built.fixedEquipmentCount,
            skippedEquipmentCount: built.skippedEquipmentCount,
            importedByKind: built.importedByKind,
            unsupportedSkus: built.unsupportedSkus,
            skippedItems: built.skippedItems,
        },
    };
}

export function createPlanFromSave(decoded: DecodedCsimSave, fileName: string): SavegamePlanResult {
    return createPlanFromDecoded(decoded, projectName(fileName), "savegame");
}

export function createPlanFromApiSnapshot(decoded: DecodedCsimSave, siteName: string): SavegamePlanResult {
    const normalizedName = siteName.trim();
    const name = (normalizedName.length === 0 ? "Imported Uptime API" : `${normalizedName} API`).slice(
        0,
        MAX_NAME_LENGTH,
    );
    return createPlanFromDecoded(decoded, name, "api");
}
